import { Modal, Form, Input } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useApiNotifications } from '@shared/hooks/useApiNotifications';

import { authApi } from '../api/authApi';

interface SelfPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

interface ChangePasswordValues {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

/**
 * Смена СВОЕГО пароля вошедшим оператором. Требует текущий пароль (сервер
 * проверяет его тем же кодом, что и вход). Пароли уходят телом через воронку
 * apiClient; в состоянии/логах не сохраняются, форма очищается после отправки.
 */
export function SelfPasswordModal({ open, onClose }: SelfPasswordModalProps) {
  const { t } = useTranslation(['auth']);
  const [form] = Form.useForm<ChangePasswordValues>();
  const { onSuccess, onError } = useApiNotifications({
    namespace: 'auth',
    errorCodeMap: {
      invalid_current_password: 'auth:changePasswordCurrentIncorrect',
    },
  });

  const mutation = useMutation<void, unknown, { oldPassword: string; newPassword: string }>({
    mutationFn: ({ oldPassword, newPassword }) => authApi.changePassword(oldPassword, newPassword),
    onSuccess: () => {
      onSuccess(t('auth:changePasswordSuccess'));
      form.resetFields();
      onClose();
    },
    onError: (error: unknown) => {
      onError(error, 'auth:changePasswordFailed');
    },
  });

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      mutation.mutate({ oldPassword: values.old_password, newPassword: values.new_password });
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t('auth:changePassword')}
      open={open}
      forceRender
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={mutation.isPending}
      okText={t('auth:changePasswordSave')}
      cancelText={t('auth:changePasswordCancel')}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="old_password"
          label={t('auth:changePasswordCurrent')}
          rules={[{ required: true, message: t('auth:changePasswordCurrentRequired') }]}
        >
          <Input.Password autoComplete="current-password" />
        </Form.Item>

        <Form.Item
          name="new_password"
          label={t('auth:changePasswordNew')}
          rules={[
            { required: true, message: t('auth:changePasswordNewRequired') },
            { min: 6, message: t('auth:changePasswordMinLength') },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          name="confirm_password"
          label={t('auth:changePasswordConfirm')}
          dependencies={['new_password']}
          rules={[
            { required: true, message: t('auth:changePasswordConfirmRequired') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('new_password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('auth:changePasswordMismatch')));
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
